import { memo } from "react";
import { ArrowRight, MessageSquareText } from "lucide-react";
import { smsHref } from "../../../api/textGuide";
import { CommandInstructions } from "./CommandInstructions";
import { CopyCommandButton } from "./CopyCommandButton";
import { pad, type JourneyCommand, type Stage } from "./journeyData";

export function SmsAction({ c, phone, testId }: { c: JourneyCommand; phone: string | null; testId: string }) {
  const cls = "j-press inline-flex min-h-[48px] w-full items-center justify-center gap-2 border-[3px] px-5 font-condensed text-base font-extrabold uppercase tracking-[0.08em]";
  if (!phone) return (
    <div>
      <button type="button" disabled data-testid={testId} className={`${cls} cursor-not-allowed border-[#3A3F48] text-[#B8BEC8]`}><MessageSquareText className="h-5 w-5" aria-hidden />Open text message</button>
      <p className="mt-1 text-xs text-[#B8BEC8]">Text number not set up yet</p>
    </div>
  );
  return (
    <a href={smsHref(phone, c.example)} data-testid={testId} className={`${cls} border-[#FFFFFF] text-[#FFFFFF] hover:bg-[#FFFFFF] hover:text-[#171A1F]`}>
      <MessageSquareText className="h-5 w-5" aria-hidden />Open text message</a>
  );
}

interface Props {
  c: JourneyCommand; index: number; total: number; phone: string | null; active: boolean;
  nextStage: Stage | null; onNextStage: (s: Stage) => void;
}

export const CommandCard = memo(function CommandCard({ c, index, total, phone, active, nextStage, onNextStage }: Props) {
  return (
    <article data-testid={`command-card-${c.slug}`} aria-roledescription="slide" aria-label={`${pad(index + 1)} of ${pad(total)}: ${c.code}`}
      aria-hidden={!active} ref={(el) => { if (el) el.toggleAttribute("inert", !active); }}
      className={`j-card flex flex-col border-[3px] bg-[#1F232A] p-5 transition-opacity duration-300 motion-reduce:transition-none sm:p-7 ${active ? "border-[#C92E2B] opacity-100" : "border-[#3A3F48] opacity-40"}`}>
      <header className="flex items-start justify-between gap-3 font-condensed text-sm font-extrabold uppercase tracking-[0.14em]">
        <span className="text-[#FFFFFF]" data-testid={`command-stage-${c.slug}`}><span className="text-[#F5D536]">{c.stage.id}</span> {c.stage.name}</span>
        <span className="shrink-0 text-[#F5D536]" data-testid={`command-position-${c.slug}`}>{pad(index + 1)} / {pad(total)}</span>
      </header>
      <h2 className="j-command mt-6 break-words text-center font-condensed font-extrabold uppercase leading-[0.85] text-[#FFFFFF]" data-testid={`command-code-${c.slug}`}>{c.code}</h2>
      <div className="mx-auto mt-3 h-[6px] w-16 bg-[#C92E2B]" aria-hidden />
      <p className="mt-3 text-center font-condensed text-xl font-extrabold uppercase tracking-[0.06em] text-[#FFFFFF] sm:text-2xl" data-testid={`command-title-${c.slug}`}>{c.title}</p>
      <p className="mx-auto mt-2 max-w-md text-center text-[15px] text-[#FFFFFF]">{c.does}</p>
      <div className="mt-6"><CommandInstructions c={c} prefix="command" /></div>
      <div className="mt-6 space-y-3">
        <CopyCommandButton text={c.example} testId={`command-copy-${c.slug}`} />
        <SmsAction c={c} phone={phone} testId={`command-sms-${c.slug}`} />
      </div>
      {nextStage && (
        <button type="button" onClick={() => onNextStage(nextStage)} data-testid={`command-next-stage-${c.slug}`}
          className="j-press mt-5 flex min-h-[44px] w-full items-center justify-between gap-2 border-t-[3px] border-[#F5D536] pt-3 font-condensed text-base font-extrabold uppercase tracking-[0.08em] text-[#F5D536]">
          <span>Next stage: {nextStage.id} {nextStage.name}</span><ArrowRight className="h-5 w-5 shrink-0" aria-hidden />
        </button>
      )}
    </article>
  );
});
